import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import NavigationBar from './NavigationBar';
import SmartEventList from '../SmartEventList/SmartEventList';
import ReviewerPanel from '../Reviewers/ReviewerPanel';
import { useSharedItem } from '../../hooks/useSharedItem';

const RightSidebar = ({
  childClicked,
  onLocationClick,
  type,
  setType,
  language,
  searchQuery, // NEW: Search query to pass to SmartEventList
  startDate,   // NEW: Start date for filtering
  endDate,     // NEW: End date for filtering
  source,              // NEW: Which collections to search
  excludeCategories,   // NEW: Categories to exclude
  categoryFilter,      // NEW: Category filter from tab config
  hasSearched,         // Distinguishes "still loading" from "genuinely empty"
  onActiveIndexChange,
  onItemsChange, // NEW: Callback to receive items from SmartEventList
  onMapSyncNeeded, // NEW: Callback for map synchronization
  userLocation,
  feedLat,
  feedLng,
  feedRadiusKm,
  onTTSToggle,
  isTTSPlaying,
  isTTSDisabled,
  audioOutputDevices,
  selectedOutputDeviceId,
  onOutputDeviceChange,
  onTTSRestart,
  selectedCreator,
  setSelectedCreator,
  creatorProfile,
  onReviewerSelect,
  onCreatorClick,
  initialReviewerFilter = null, // category filter carried in by a shared link
}) => {
  // Local state to keep track of items count
  const [displayedItems, setDisplayedItems] = useState([]);

  const handleInternalItemsChange = (items) => {
    setDisplayedItems(items);
    if (onItemsChange) onItemsChange(items);
  };

  // ผู้รีวิว tab renders its own panel (reviewer directory + that reviewer's items)
  const isReviewerTab = type === 'Reviewers';

  // Expanded item overlay. A shared link (?item=…) comes from the URL, so it is
  // correct on the first paint of a cold browser and — unlike local state — it
  // survives the remount this list does whenever the category tab changes.
  // Anything opened from the list itself stays local.
  const { sharedPlace, closeShared } = useSharedItem();
  const [localExpandedPlace, setLocalExpandedPlace] = useState(null);
  const expandedPlace = sharedPlace || localExpandedPlace;

  // Closing a URL-driven overlay means navigating; clearing state would leave
  // it on screen. closeShared() no-ops when no shared link is open.
  const closeExpanded = useCallback(() => {
    setLocalExpandedPlace(null);
    closeShared();
  }, [closeShared]);

  const openExpanded = useCallback((place) => {
    closeShared();
    setLocalExpandedPlace(place);
  }, [closeShared]);

  // The list is keyed on the active collections, so switching category tab
  // remounts it. Close the overlay with it, matching mobile — but not on the
  // first run, or a shared link would dismiss itself during mount.
  const listKey = `v-${Array.isArray(source) ? source.join('-') : source}-${Array.isArray(excludeCategories) ? excludeCategories.join('-') : ''}`;
  const listKeyGuardRef = useRef(true);
  useEffect(() => {
    if (listKeyGuardRef.current) {
      listKeyGuardRef.current = false;
      return;
    }
    closeExpanded();
  }, [listKey, closeExpanded]);

  // ผู้รีวิว swaps the whole list out for ReviewerPanel, which has no overlay —
  // a shared link left in the URL there would silently vanish and come back on
  // the way out. Close it instead. (No-op on mount: the tab starts on Explore.)
  useEffect(() => {
    if (isReviewerTab) closeShared();
  }, [isReviewerTab, closeShared]);

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      width: '100%'
    }}>
      {/* Navigation Bar */}
      <Box sx={{ flexShrink: 0 }}>
        <NavigationBar
          type={type}
          setType={setType}
          language={language}
          onTTSToggle={onTTSToggle}
          isTTSPlaying={isTTSPlaying}
          isTTSDisabled={isTTSDisabled}
          audioOutputDevices={audioOutputDevices}
          selectedOutputDeviceId={selectedOutputDeviceId}
          onOutputDeviceChange={onOutputDeviceChange}
          onTTSRestart={onTTSRestart}
        />
      </Box>

      {/* Reviewer directory tab — its own panel, not the geo feed */}
      {isReviewerTab ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden', width: '100%', minHeight: 0 }}>
          <ReviewerPanel
            selectedReviewer={selectedCreator}
            onSelectReviewer={onReviewerSelect || setSelectedCreator}
            onClearReviewer={() => setSelectedCreator(null)}
            profile={creatorProfile}
            language={language}
            initialFilter={initialReviewerFilter}
            layout="vertical"
            childClicked={childClicked}
            onLocationClick={onLocationClick}
            onActiveIndexChange={onActiveIndexChange}
            onItemsChange={handleInternalItemsChange}
          />
        </Box>
      ) : (
      /* Event List - takes remaining space */
      <Box sx={{
        flexGrow: 1,
        overflow: 'hidden',
        padding: 0,
        width: '100%'
      }}>
        <SmartEventList
          key={listKey}
          layout="vertical"
          context="explore"
          feedMode={true} // CRITICAL: Enable feed batching system
          enabled={feedLat !== null && feedLat !== undefined}
          userLocation={userLocation}
          lat={feedLat}
          lng={feedLng}
          radiusKm={feedRadiusKm ?? undefined}
          searchQuery={searchQuery} // NEW: Pass search query to trigger list refresh
          category={categoryFilter || undefined} // NEW: Use tab's categoryFilter
          source={source}                   // NEW: Which collections to search
          excludeCategories={excludeCategories} // NEW: Categories to exclude
          startDate={startDate} // NEW: Pass start date for filtering
          endDate={endDate}     // NEW: Pass end date for filtering
          hasSearched={hasSearched}
          childClicked={childClicked}
          onLocationClick={onLocationClick}
          onActiveIndexChange={onActiveIndexChange}
          onItemsChange={handleInternalItemsChange} // Use internal wrapper to get item count
          onMapSyncNeeded={onMapSyncNeeded} // NEW: Pass callback through
          language={language}
          onCreatorClick={onCreatorClick} // Reviewer name on a card → ผู้รีวิว tab
          expandedPlace={expandedPlace}
          onExpand={openExpanded}
          onExpandClose={closeExpanded}
        />
      </Box>
      )}
    </Box>
  );
};

export default RightSidebar;